import { Box, Grid2 as Grid, Link, Stack, Typography } from '@mui/material';
import { Dropdown, TextInput } from 'framework/form';
import { useShowContext } from 'framework/show';
import { DATE_FORMAT, useDateService } from 'framework/utils';
import { Tooltip } from 'framework/ui/mui-builder';
import moment from 'moment';
import UpdateIcon from '@mui/icons-material/Update';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import { ProjectEngineeringGraphqlData } from '../types';
import { DELIVERY_MODEL_OPTIONS, NOT_DEFINED, OWNERSHIP_OPTIONS, getOptionLabel } from '../constants';
import EngineeringSection from '../shared/EngineeringSection';
import PpcLinkConfirmBanner from './PpcLinkConfirmBanner';

const source = 'engineeringPolicy';

const productionOwnershipTooltip =
  'Defines whether our team is responsible for production. If our team owns production, incident handling and user-reported bug tracking are required.';

const NotDefinedValue = () => (
  <Box color="text.error" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <Typography component="span" variant="body1">
      {NOT_DEFINED}
    </Typography>
    <PriorityHighIcon sx={{ width: 20, height: 20 }} />
  </Box>
);

const EngineeringConfiguration = () => {
  const { data: showData } = useShowContext<ProjectEngineeringGraphqlData>();
  const { getToday, datesDifference } = useDateService();

  const engineeringPolicy = showData.getData()?.engineeringPolicy;
  const policyUpdatedAt = engineeringPolicy?.updatedAt ?? engineeringPolicy?.createdAt;
  const ppcLink = engineeringPolicy?.ppcLink ?? '';
  const ppcLinkUpdatedAt = engineeringPolicy?.ppcLinkUpdatedAt;

  const renderUpdatedTime = (date?: string | null) => {
    if (!date) return null;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Tooltip title={moment(date).format(DATE_FORMAT.API_ONLY_DATE)} placement="bottom">
          <UpdateIcon sx={{ color: 'text.secondary', width: 20, height: 20 }} />
        </Tooltip>
        <Typography variant="body1">{datesDifference(getToday(), date)}</Typography>
      </Box>
    );
  };

  return (
    <EngineeringSection
      title="Engineering сonfiguration"
      editContent={
        <Stack spacing={3}>
          <Typography variant="body1">Configure engineering setup and practices for the project</Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack spacing={2}>
                <Typography variant="body2" color="textSecondary">
                  Production ownership defines whether incident and bug tracking is required
                </Typography>
                <Dropdown
                  source={`${source}.productionOwnership`}
                  label="Production ownership"
                  tooltip={productionOwnershipTooltip}
                  options={OWNERSHIP_OPTIONS}
                  placeholder="Select"
                />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack spacing={2}>
                <Typography variant="body2" color="textSecondary">
                  Defines the project delivery setup and process level
                </Typography>
                <Dropdown
                  source={`${source}.deliveryModel`}
                  label="Delivery model"
                  options={DELIVERY_MODEL_OPTIONS}
                  placeholder="Select"
                />
              </Stack>
            </Grid>
            <Grid size={6}>
              <Stack spacing={2}>
                <Typography variant="body2" color="textSecondary">
                  Defines the project engineering practices and processes
                </Typography>
                <TextInput source={`${source}.ppcLink`} label="Project practice configuration" />
              </Stack>
            </Grid>
          </Grid>
        </Stack>
      }
      viewContent={
        <Grid container spacing={12}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Stack spacing={1}>
              <Box display="flex" alignItems="center">
                <Box
                  sx={{
                    width: '220px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    flexShrink: 0,
                  }}
                >
                  <Typography component="span" color="textSecondary" variant="body1">
                    Production ownership:
                  </Typography>
                  <Tooltip title={productionOwnershipTooltip} placement="top">
                    <InfoIcon sx={{ width: 16, height: 16, color: 'text.secondary' }} />
                  </Tooltip>
                </Box>
                <Box flex="1">
                  {engineeringPolicy?.productionOwnership ? (
                    <Typography component="span" variant="body1">
                      {getOptionLabel(OWNERSHIP_OPTIONS, engineeringPolicy.productionOwnership)}
                    </Typography>
                  ) : (
                    <NotDefinedValue />
                  )}
                </Box>
                {renderUpdatedTime(policyUpdatedAt)}
              </Box>
              <Box display="flex" alignItems="center">
                <Typography component="span" color="textSecondary" variant="body1" width="220px" flexShrink={0}>
                  Delivery model:
                </Typography>
                <Box flex="1">
                  {engineeringPolicy?.deliveryModel ? (
                    <Typography component="span" variant="body1">
                      {getOptionLabel(DELIVERY_MODEL_OPTIONS, engineeringPolicy.deliveryModel)}
                    </Typography>
                  ) : (
                    <NotDefinedValue />
                  )}
                </Box>
              </Box>
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Stack spacing={1.5}>
              <Box display="flex" alignItems="center">
                <Typography component="span" color="textSecondary" variant="body1" width="252px" flexShrink={0}>
                  Project practice configuration:
                </Typography>
                <Box flex="1">
                  {ppcLink ? (
                    <Link href={ppcLink} target="_blank" rel="noreferrer" variant="body1">
                      link
                    </Link>
                  ) : (
                    <NotDefinedValue />
                  )}
                </Box>
                {renderUpdatedTime(ppcLinkUpdatedAt)}
              </Box>
              <PpcLinkConfirmBanner />
            </Stack>
          </Grid>
        </Grid>
      }
    />
  );
};

export default EngineeringConfiguration;
